import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  FORCE_DARK_THEME,
  THEME_STORAGE_KEY,
  applyTheme,
  getStoredTheme,
  type ThemeMode,
} from '../theme/initTheme';

type ThemeContextValue = {
  theme: ThemeMode;
  isDark: boolean;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeMode>(() => getStoredTheme());

  const setTheme = useCallback((next: ThemeMode) => {
    if (FORCE_DARK_THEME) {
      setThemeState('dark');
      applyTheme('dark');
      return;
    }
    setThemeState(next);
    applyTheme(next);
    window.localStorage.setItem(THEME_STORAGE_KEY, next);
  }, []);

  const toggleTheme = useCallback(() => {
    if (FORCE_DARK_THEME) return;
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [setTheme, theme]);

  useEffect(() => {
    applyTheme(FORCE_DARK_THEME ? 'dark' : theme);
  }, [theme]);

  const value = useMemo(
    () => ({
      theme: FORCE_DARK_THEME ? 'dark' : theme,
      isDark: FORCE_DARK_THEME ? true : theme === 'dark',
      setTheme,
      toggleTheme,
    }),
    [setTheme, theme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
